import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, ArrowLeft, Edit2, Trash2, Upload } from 'lucide-react';
import { useState, useEffect } from 'react';
import { glossaryApi } from '../api/glossary';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Textarea from '../components/Textarea';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import type { CreateGlossaryTermRequest, GlossaryTerm } from '../types';
import { importGlossaryFromXlsx } from '../utils/importGlossaryFromXlsx';

export default function GlossaryPage() {
  const { seriesId } = useParams<{ seriesId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTerm, setEditingTerm] = useState<GlossaryTerm | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importParse, setImportParse] = useState<
    | null
    | Awaited<ReturnType<typeof importGlossaryFromXlsx>>
  >(null);
  const [importUpdateExisting, setImportUpdateExisting] = useState(true);
  const [importOverwriteEmpty, setImportOverwriteEmpty] = useState(false);
  const [sourceTerm, setSourceTerm] = useState('');
  const [ruTerm, setRuTerm] = useState('');
  const [attribute, setAttribute] = useState('');
  const [declensionPolicy, setDeclensionPolicy] = useState<'none' | 'wrapper_only' | 'name'>('name');
  const [notes, setNotes] = useState('');
  const [originalVariations, setOriginalVariations] = useState<string[]>([]);
  const [translatedVariations, setTranslatedVariations] = useState<string[]>([]);
  const [gender, setGender] = useState<string | null>(null);
  const [firstChapterNo, setFirstChapterNo] = useState<number | null>(null);

  const normalizeKey = (input: string) =>
    String(input ?? '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const { data: glossary, isLoading, error } = useQuery({
    queryKey: ['glossary', seriesId],
    queryFn: () => {
      console.log('Fetching glossary for series:', seriesId);
      return glossaryApi.getBySeries(seriesId!);
    },
    enabled: !!seriesId,
    retry: 2,
  });

  useEffect(() => {
    if (glossary) {
      console.log('Glossary loaded:', glossary);
    }
  }, [glossary]);

  useEffect(() => {
    if (error) {
      console.error('Error loading glossary:', error);
    }
  }, [error]);

  const createMutation = useMutation({
    mutationFn: (data: CreateGlossaryTermRequest) => glossaryApi.create(seriesId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['glossary', seriesId] });
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateGlossaryTermRequest }) =>
      glossaryApi.update(seriesId!, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['glossary', seriesId] });
      resetForm();
    },
  });

  const bulkImportMutation = useMutation({
    mutationFn: (payload: { items: CreateGlossaryTermRequest[]; options: { updateExisting: boolean; overwriteEmpty: boolean } }) =>
      glossaryApi.bulkImport(seriesId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['glossary', seriesId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => glossaryApi.delete(seriesId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['glossary', seriesId] });
    },
  });

  const handleDelete = (term: GlossaryTerm) => {
    if (window.confirm(`Вы уверены, что хотите удалить термин "${term.source_term}"?`)) {
      deleteMutation.mutate(term.id);
    }
  };

  const resetForm = () => {
    setIsModalOpen(false);
    setEditingTerm(null);
    setSourceTerm('');
    setRuTerm('');
    setAttribute('');
    setDeclensionPolicy('name');
    setGender(null);
    setNotes('');
    setOriginalVariations([]);
    setTranslatedVariations([]);
    setFirstChapterNo(null);
  };

  const resetImport = () => {
    setIsImportModalOpen(false);
    setImportFile(null);
    setImportParse(null);
    setImportUpdateExisting(true);
    setImportOverwriteEmpty(false);
  };

  const handleEdit = (term: GlossaryTerm) => {
    setEditingTerm(term);
    setSourceTerm(term.source_term);
    setRuTerm(term.ru_term);
    setAttribute(term.attribute || '');
    setDeclensionPolicy(term.declension_policy || 'name');
    setGender(term.gender || null);
    setNotes(term.notes || '');
    setOriginalVariations(term.original_variations || []);
    setTranslatedVariations(term.translated_variations || []);
    setFirstChapterNo(term.first_chapter_no ?? null);
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: CreateGlossaryTermRequest = {
      source_term: sourceTerm,
      ru_term: ruTerm,
      attribute: attribute || undefined,
      declension_policy: declensionPolicy,
      gender: (gender as CreateGlossaryTermRequest['gender']) || undefined,
      notes: notes || undefined,
      original_variations: originalVariations.filter(v => v.trim()).length > 0
        ? originalVariations.filter(v => v.trim())
        : undefined,
      translated_variations: translatedVariations.filter(v => v.trim()).length > 0
        ? translatedVariations.filter(v => v.trim())
        : undefined,
      first_chapter_no: firstChapterNo ?? undefined,
    };

    if (editingTerm) {
      updateMutation.mutate({ id: editingTerm.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handlePickImportFile = async (file: File | null) => {
    setImportFile(file);
    setImportParse(null);
    if (!file) return;
    try {
      const parsed = await importGlossaryFromXlsx(file);
      setImportParse(parsed);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      setImportParse({
        items: [],
        rows: [],
        errors: [{ rowIndex: 0, message }],
        conflicts: [],
      });
    }
  };

  const buildExistingByNorm = () => {
    const byNorm = new Map<string, GlossaryTerm[]>();
    for (const t of Array.isArray(glossary) ? glossary : []) {
      const key = normalizeKey(t.source_term);
      const list = byNorm.get(key) ?? [];
      list.push(t);
      byNorm.set(key, list);
    }
    return byNorm;
  };

  const existingByNorm = buildExistingByNorm();
  const existingConflictsCount = Array.from(existingByNorm.values()).filter((v) => v.length > 1).length;

  const preview = (() => {
    if (!importParse) return null;
    let willInsert = 0;
    let willUpdate = 0;
    let unchanged = 0;
    let skipped = 0;
    let blockedByDbConflict = 0;

    for (const item of importParse.items) {
      const key = normalizeKey(item.source_term);
      const existing = existingByNorm.get(key) ?? [];
      if (existing.length > 1) {
        blockedByDbConflict++;
        continue;
      }
      const current = existing[0];
      if (!current) {
        willInsert++;
        continue;
      }
      if (!importUpdateExisting) {
        skipped++;
        continue;
      }

      const nextRu = item.ru_term.trim();
      const nextAttr = (item.attribute ?? '').trim();
      const nextNotes = (item.notes ?? '').trim();
      const nextDecl = item.declension_policy ?? 'name';

      const currAttr = (current.attribute ?? '').trim();
      const currNotes = (current.notes ?? '').trim();
      const currDecl = current.declension_policy ?? 'name';

      const resolvedRu = nextRu;
      const resolvedAttr = importOverwriteEmpty ? nextAttr : nextAttr || currAttr;
      const resolvedNotes = importOverwriteEmpty ? nextNotes : nextNotes || currNotes;

      const changed =
        normalizeKey(current.ru_term) !== normalizeKey(resolvedRu) ||
        normalizeKey(currAttr) !== normalizeKey(resolvedAttr) ||
        normalizeKey(currNotes) !== normalizeKey(resolvedNotes) ||
        currDecl !== nextDecl;

      if (changed) willUpdate++;
      else unchanged++;
    }

    return { willInsert, willUpdate, unchanged, skipped, blockedByDbConflict };
  })();

  const handleApplyImport = () => {
    if (!importParse) return;
    const items = importParse.items.filter((it) => {
      const key = normalizeKey(it.source_term);
      const existing = existingByNorm.get(key) ?? [];
      return existing.length <= 1;
    });
    bulkImportMutation.mutate({
      items,
      options: { updateExisting: importUpdateExisting, overwriteEmpty: importOverwriteEmpty },
    });
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="Ошибка загрузки глоссария" />;

  return (
    <div>
      <div className="mb-6">
        <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Назад
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Глоссарий</h1>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <Button className="w-full sm:w-auto flex items-center justify-center" variant="secondary" onClick={() => setIsImportModalOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Импорт XLSX
          </Button>
          <Button className="w-full sm:w-auto flex items-center justify-center" onClick={() => setIsModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Добавить термин
          </Button>
        </div>
      </div>

      {!glossary || (Array.isArray(glossary) && glossary.length === 0) ? (
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">Глоссарий пуст</p>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="sm:hidden space-y-3">
            {Array.isArray(glossary) &&
              glossary.map((term) => (
                <div
                  key={term.id}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white break-words">
                        {term.source_term}
                      </div>
                      <div className="mt-1 text-sm text-primary-600 dark:text-primary-400 break-words">
                        {term.ru_term}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEdit(term)}
                        className="p-2 rounded-md text-primary-600 hover:bg-primary-50 hover:text-primary-900 dark:text-primary-400 dark:hover:bg-primary-900/20 dark:hover:text-primary-300"
                        title="Редактировать"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(term)}
                        disabled={deleteMutation.isPending}
                        className="p-2 rounded-md text-red-600 hover:bg-red-50 hover:text-red-800 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300 disabled:opacity-50"
                        title="Удалить"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {term.gender && (
                      <span
                        className={`text-xs px-2 py-1 rounded ${term.gender === 'female' ? 'bg-pink-100 dark:bg-pink-900 text-pink-800 dark:text-pink-200' : term.gender === 'male' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
                      >
                        {term.gender === 'female' ? '♀' : term.gender === 'male' ? '♂' : '⚬'} {term.gender}
                      </span>
                    )}
                    {term.attribute ? (
                      <span className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                        {term.attribute}
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                        —
                      </span>
                    )}
                    <span className="text-xs px-2 py-1 rounded bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                      {term.declension_policy || 'name'}
                    </span>
                    {term.original_variations && term.original_variations.length > 0 && (
                      <span className="text-xs px-2 py-1 rounded bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                        +{term.original_variations.length} вар.
                      </span>
                    )}
                    {term.first_chapter_no != null && (
                      <span className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                        Гл.{term.first_chapter_no}
                      </span>
                    )}
                  </div>

                  {term.notes ? (
                    <div className="mt-3 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-words">
                      {term.notes}
                    </div>
                  ) : (
                    <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">—</div>
                  )}
                </div>
              ))}
          </div>

          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Оригинальное название
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Русское название
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Пол/Атрибут
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Правило склонения
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Описание
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {Array.isArray(glossary) &&
                  glossary.map((term) => (
                    <tr key={term.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        <div className="flex items-center gap-2">
                          <span>{term.source_term}</span>
                          {term.original_variations && term.original_variations.length > 0 && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 cursor-help"
                              title={`Вариации: ${term.original_variations.join(', ')}`}
                            >
                              +{term.original_variations.length}
                            </span>
                          )}
                          {term.first_chapter_no != null && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                              title={`Первое упоминание: глава ${term.first_chapter_no}`}
                            >
                              Гл.{term.first_chapter_no}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-600 dark:text-primary-400">
                        {term.ru_term}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                          {term.gender && (
                            <span
                              className={`text-base ${term.gender === 'female' ? 'text-pink-500' : term.gender === 'male' ? 'text-blue-500' : 'text-gray-400'}`}
                              title={`Пол: ${term.gender}`}
                            >
                              {term.gender === 'female' ? '♀' : term.gender === 'male' ? '♂' : '⚬'}
                            </span>
                          )}
                          <span>{term.attribute || '-'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <span className="px-2 py-1 text-xs rounded bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                          {term.declension_policy || 'name'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {term.notes || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEdit(term)}
                            className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                            title="Редактировать"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(term)}
                            disabled={deleteMutation.isPending}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            title="Удалить"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={resetForm}
        title={editingTerm ? 'Редактировать термин' : 'Добавить термин'}
        footer={
          <>
            <Button variant="secondary" onClick={resetForm}>
              Отмена
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!sourceTerm.trim() || !ruTerm.trim() || createMutation.isPending || updateMutation.isPending}
            >
              {editingTerm ? 'Сохранить' : 'Добавить'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Оригинальное название"
            value={sourceTerm}
            onChange={(e) => setSourceTerm(e.target.value)}
            required
            placeholder="Введите оригинальное название"
          />
          <Input
            label="Русское название"
            value={ruTerm}
            onChange={(e) => setRuTerm(e.target.value)}
            required
            placeholder="Введите русское или переведённое название"
          />
          <Input
            label="Пол/Атрибут/Принадлежность (необязательно)"
            value={attribute}
            onChange={(e) => setAttribute(e.target.value)}
            placeholder="Например: персонаж, предмет, способность"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Правило склонения
            </label>
            <select
              value={declensionPolicy}
              onChange={(e) => setDeclensionPolicy(e.target.value as 'none' | 'wrapper_only' | 'name')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="name">name - можно склонять по правилам русского языка</option>
              <option value="wrapper_only">wrapper_only - склонение только через окружающие слова</option>
              <option value="none">none - неизменяемый термин</option>
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Определяет, как можно изменять термин при переводе
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Пол персонажа (необязательно)
            </label>
            <select
              value={gender || ''}
              onChange={(e) => setGender(e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">— не указан —</option>
              <option value="male">male - мужской</option>
              <option value="female">female - женский</option>
              <option value="neutral">neutral - нейтральный</option>
              <option value="unknown">unknown - неизвестный</option>
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Используется для правильного рода глаголов и прилагательных в переводе
            </p>
          </div>
          <Textarea
            label="Краткое описание (необязательно)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Краткое описание термина"
          />

          {/* Вариации имён */}
          <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Вариации имени (необязательно)
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Укажите альтернативные формы обращения (никнеймы, сокращения, с суффиксами).
              Количество оригинальных и переведённых вариаций должно совпадать.
            </p>

            <div className="space-y-3">
              {originalVariations.map((origVar, idx) => (
                <div key={idx} className="flex gap-2 items-start">
                  <div className="flex-1">
                    <Input
                      label={idx === 0 ? 'Оригинал' : undefined}
                      value={origVar}
                      onChange={(e) => {
                        const newVars = [...originalVariations];
                        newVars[idx] = e.target.value;
                        setOriginalVariations(newVars);
                      }}
                      placeholder="田中さん"
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      label={idx === 0 ? 'Перевод' : undefined}
                      value={translatedVariations[idx] || ''}
                      onChange={(e) => {
                        const newVars = [...translatedVariations];
                        newVars[idx] = e.target.value;
                        setTranslatedVariations(newVars);
                      }}
                      placeholder="Танака-сан"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setOriginalVariations(originalVariations.filter((_, i) => i !== idx));
                      setTranslatedVariations(translatedVariations.filter((_, i) => i !== idx));
                    }}
                    className={`text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-2 ${idx === 0 ? 'mt-6' : ''}`}
                    title="Удалить вариацию"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}

              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setOriginalVariations([...originalVariations, '']);
                  setTranslatedVariations([...translatedVariations, '']);
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Добавить вариацию
              </Button>
            </div>
          </div>

          {/* Номер главы первого упоминания */}
          <div className="mt-4">
            <Input
              label="Номер главы первого упоминания (необязательно)"
              type="number"
              value={firstChapterNo?.toString() || ''}
              onChange={(e) => {
                const val = e.target.value;
                setFirstChapterNo(val ? parseInt(val, 10) : null);
              }}
              placeholder="1"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Помогает модели понять контекст появления термина
            </p>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isImportModalOpen}
        onClose={resetImport}
        title="Импорт глоссария из XLSX"
        footer={
          <>
            <Button variant="secondary" onClick={resetImport}>
              Отмена
            </Button>
            <Button
              onClick={handleApplyImport}
              disabled={
                !importParse ||
                importParse.items.length === 0 ||
                bulkImportMutation.isPending ||
                !seriesId
              }
            >
              Применить импорт
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Файл .xlsx
            </label>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => handlePickImportFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            />
            {importFile ? (
              <div className="text-xs text-gray-500 dark:text-gray-400">{importFile.name}</div>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={importUpdateExisting}
                onChange={(e) => setImportUpdateExisting(e.target.checked)}
              />
              Обновлять существующие термины
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={importOverwriteEmpty}
                onChange={(e) => setImportOverwriteEmpty(e.target.checked)}
              />
              Перезаписывать пустыми значениями
            </label>
          </div>

          {preview ? (
            <Card>
              <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                <div>Будет добавлено: {preview.willInsert}</div>
                <div>Будет обновлено: {preview.willUpdate}</div>
                <div>Без изменений: {preview.unchanged}</div>
                <div>Пропущено: {preview.skipped}</div>
                <div>Конфликты в файле: {importParse?.conflicts.length ?? 0}</div>
                <div>Ошибки строк: {importParse?.errors.length ?? 0}</div>
                <div>Неоднозначные совпадения в текущем глоссарии: {existingConflictsCount}</div>
                <div>Строки, заблокированные из-за неоднозначности: {preview.blockedByDbConflict}</div>
              </div>
            </Card>
          ) : null}

          {importParse?.errors?.length ? (
            <Card>
              <div className="text-sm font-medium text-gray-900 dark:text-white mb-2">Ошибки</div>
              <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                {importParse.errors.slice(0, 30).map((e, idx) => (
                  <div key={`${e.rowIndex}-${idx}`}>
                    Строка {e.rowIndex}: {e.message}
                  </div>
                ))}
                {importParse.errors.length > 30 ? (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Показаны первые 30 ошибок
                  </div>
                ) : null}
              </div>
            </Card>
          ) : null}

          {importParse?.conflicts?.length ? (
            <Card>
              <div className="text-sm font-medium text-gray-900 dark:text-white mb-2">Конфликты в файле</div>
              <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                {importParse.conflicts.slice(0, 20).map((c) => (
                  <div key={c.normalizedKey} className="space-y-1">
                    <div className="font-medium">{c.normalizedKey}</div>
                    {c.rows.map((r) => (
                      <div key={r.rowIndex} className="text-xs text-gray-600 dark:text-gray-400">
                        Строка {r.rowIndex}: {r.source_term} → {r.ru_term}
                      </div>
                    ))}
                  </div>
                ))}
                {importParse.conflicts.length > 20 ? (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Показаны первые 20 конфликтов
                  </div>
                ) : null}
              </div>
            </Card>
          ) : null}

          {bulkImportMutation.data ? (
            <Card>
              <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                <div>Добавлено: {bulkImportMutation.data.inserted}</div>
                <div>Обновлено: {bulkImportMutation.data.updated}</div>
                <div>Без изменений: {bulkImportMutation.data.unchanged}</div>
                <div>Пропущено: {bulkImportMutation.data.skipped}</div>
                <div>Ошибок на сервере: {bulkImportMutation.data.errors.length}</div>
                <div>Конфликтов на сервере: {bulkImportMutation.data.conflicts.length}</div>
              </div>
            </Card>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
